"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EthCandyShop = void 0;
const constants_1 = require("@opensea/seaport-js/lib/constants");
const ethers_1 = require("ethers");
const candy_shop_types_1 = require("@liqnft/candy-shop-types");
const BaseShop_1 = require("../base/BaseShop");
const BaseShopModel_1 = require("../base/BaseShopModel");
const eth_1 = require("../../factory/conveyor/eth");
const vendor_1 = require("../../vendor");
const CandyShopInfoAPI_1 = require("../../CandyShopInfoAPI");
const DEFAULT_PRICE_DECIMALS = 3;
const DEFAULT_PRICE_DECIMALS_MIN = 0;
const DEFAULT_VOLUME_DECIMALS = 1;
const DEFAULT_VOLUME_DECIMALS_MIN = 0;
const Logger = 'EthCandyShop';
/**
 * @class EthCandyShop with private constructor, use public static method to instantiate the object.
 */
class EthCandyShop extends BaseShop_1.BaseShop {
    constructor(shopAddress, params) {
        const { programId, shopCreatorAddress, treasuryMint, env, settings } = params;
        const baseShopParams = {
            programId,
            shopCreatorAddress,
            treasuryMint,
            env: env !== null && env !== void 0 ? env : candy_shop_types_1.Blockchain.Eth,
            settings
        };
        // Apply common params to BaseShop
        super(baseShopParams);
        this._candyShopAddress = shopAddress;
        this.ethereumPort = new eth_1.EthereumPort();
        console.log(`${Logger} constructor: instantiated EthCandyShop=`, this);
    }
    /**
     * Involve the asynchronous fetch for required shop details to create EthCandyShop.
     * @returns EthCandyShop
     */
    static async initEthCandyShop(params) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
        // Must config the endpoint before calling any CandyShop API
        BaseShop_1.BaseShop.configEndpoint(params.env);
        // Fetch required details for EVM setup
        const shopDetail = await (0, vendor_1.safeAwait)((0, CandyShopInfoAPI_1.fetchShopsByIdentifier)(params.shopCreatorAddress, params.treasuryMint, params.programId));
        if (shopDetail.error || !shopDetail.result || !shopDetail.result.success) {
            throw new Error(`${Logger} init, fetchShopsByIdentifier failed=${(_a = shopDetail.result) === null || _a === void 0 ? void 0 : _a.msg}`);
        }
        const shopResponse = shopDetail.result.result;
        // Assign settings if any or fallback to default
        const candyShopSettings = {
            currencySymbol: shopResponse.symbol,
            currencyDecimals: shopResponse.decimals,
            priceDecimals: (_c = (_b = params.settings) === null || _b === void 0 ? void 0 : _b.priceDecimals) !== null && _c !== void 0 ? _c : DEFAULT_PRICE_DECIMALS,
            priceDecimalsMin: (_e = (_d = params.settings) === null || _d === void 0 ? void 0 : _d.priceDecimalsMin) !== null && _e !== void 0 ? _e : DEFAULT_PRICE_DECIMALS_MIN,
            volumeDecimals: (_g = (_f = params.settings) === null || _f === void 0 ? void 0 : _f.volumeDecimals) !== null && _g !== void 0 ? _g : DEFAULT_VOLUME_DECIMALS,
            volumeDecimalsMin: (_j = (_h = params.settings) === null || _h === void 0 ? void 0 : _h.volumeDecimalsMin) !== null && _j !== void 0 ? _j : DEFAULT_VOLUME_DECIMALS_MIN,
            // TBD: If EthCandyShop doesn't need connectionUrl, should not provide it in common ShopSettings
            connectionUrl: (_l = (_k = params.settings) === null || _k === void 0 ? void 0 : _k.connectionUrl) !== null && _l !== void 0 ? _l : '',
            connectionConfig: (_m = params.settings) === null || _m === void 0 ? void 0 : _m.connectionConfig,
            explorerLink: (_p = (_o = params.settings) === null || _o === void 0 ? void 0 : _o.explorerLink) !== null && _p !== void 0 ? _p : BaseShopModel_1.ExplorerLinkBase.Polygon
        };
        const ethParams = {
            ...params,
            settings: candyShopSettings
        };
        const ethCandyShop = new EthCandyShop(shopResponse.candyShopAddress, ethParams);
        return ethCandyShop;
    }
    get candyShopAddress() {
        return this._candyShopAddress;
    }
    get explorerLink() {
        return this._settings.explorerLink;
    }
    get version() {
        return BaseShopModel_1.CandyShopVersion.V1;
    }
    /**
     * 1. Perform validation and check if user has enough token allowance and has enough balance.
     * 2. If validation does not pass, try to get users to approve more allowance.
     * 3. Fulfill the order.
     * @param params
     * @returns
     */
    async buy(params) {
        const { providers, orderUuid } = params;
        const provider = new ethers_1.ethers.providers.Web3Provider(providers);
        const wallet = provider.getSigner();
        const txHash = await this.ethereumPort.fulfillOrder(wallet, orderUuid);
        return txHash;
    }
    /**
     * 1. Get user to approve the NFT token allowance.
     * 2. Execute Order request for order registration.
     * @param params
     * @property {number} price: user input value on the UI with minimum value with maximum number of fraction is 3
     * @returns
     */
    async sell(params) {
        const { providers, nft, price } = params;
        const provider = new ethers_1.ethers.providers.Web3Provider(providers);
        const wallet = provider.getSigner();
        const shopResult = await this.ethereumPort.getShopByUuid(this.candyShopAddress);
        const shop = shopResult.result;
        const offerer = await wallet.getAddress();
        const priceValue = ethers_1.ethers.BigNumber.from(price * 1000)
            .mul(ethers_1.ethers.BigNumber.from(10).pow(this.currencyDecimals))
            .div(1000)
            .toString();
        const consideration = this.ethereumPort.getConsiderationFromOrder(offerer, { address: shop.paymentAssets[0].address, type: shop.paymentAssets[0].type, value: priceValue }, shop);
        const itemType = nft.itemType === 'ERC1155' ? constants_1.ItemType.ERC1155 : constants_1.ItemType.ERC721;
        const offer = [
            {
                identifier: nft.tokenAccountAddress,
                itemType,
                token: nft.tokenMintAddress.split(':')[0],
                amount: '1'
            }
        ];
        const executeOrderParams = {
            shopUuid: shop.uuid,
            signer: wallet,
            createOrderInput: {
                offer,
                consideration
            },
            accountAddress: offerer
        };
        const OrderUuid = await this.ethereumPort.executeOrder(executeOrderParams);
        return OrderUuid;
    }
    async cancel(params) {
        const { providers, orderUuid } = params;
        const provider = new ethers_1.ethers.providers.Web3Provider(providers);
        const wallet = provider.getSigner();
        const txHash = await this.ethereumPort.cancelOrder(wallet, orderUuid);
        return txHash;
    }
    getNftPurchasePayload(params) {
        const { buyerAddress, orderUuid } = params;
        return this.ethereumPort.getFulfillOrderPayloadByUuidAndBuyerAddress(orderUuid, buyerAddress);
    }
}
exports.EthCandyShop = EthCandyShop;
